/**
 * Die Bildblase und der runde Teilen-Knopf neben ihr. (01.10.2026, Ticket 2892)
 *
 * Aus app.ts herausgezogen, damit sie ohne die ganze App prüfbar ist. Zusammengesetzt wird die Blase hier,
 * eingeordnet (data-ts, Uhrzeit, Kanalmarke, Tagestrenner) wird sie weiter dort, wo alle Blasen eingeordnet
 * werden — `insertOrdered` in app.ts.
 *
 * Eine Blase, in dieser Reihenfolge (Telegram-Vorbild):
 *   1. das Bild (oben, mit Radius; Platz vorab reserviert über `bildmass.ts` — sonst springt der Verlauf, Ticket 2873),
 *   2. die Beschriftung DARUNTER (nur wenn es eine gibt),
 *   3. der Teilen-Knopf (liegt per CSS NEBEN der Blase, nicht darin),
 *   4. am Ende hängt `insertOrdered` die Uhrzeit an.
 *
 * Ein Tipp auf das Bild vergrößert es (Lightbox, `vergroessern`); das Bild ist dafür auch mit der Tastatur
 * erreichbar. Ein Bild, das nicht lädt, zeigt weiter seinen Alt-Text — die Blase bleibt, ihre Höhe auch.
 */
import { ruesteBildAus } from "./bildmass.js";
import { shareMedia, type ShareInput } from "./share.js";
import { zeichen } from "./zeichen.js";
import { hatTransparenz, makeDownloadButton } from "./bild-werkzeuge.js";

/**
 * Teilen-Knopf für Medien-Blasen: ein echter `<button>` mit Beschriftung. Ein Tipp öffnet das Teilen-Blatt des
 * Geräts (Web Share API, bei Dateien mit der Datei, sonst mit der Adresse); ohne diese API ein Rückfall-Blatt
 * mit „Link kopieren“ (lib/share.ts). `teilen` ist nur für Tests austauschbar.
 */
export function makeShareButton(
  input: ShareInput,
  teilen: (eingabe: ShareInput) => Promise<void> = shareMedia,
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "bubble-share";
  btn.setAttribute("aria-label", "Teilen");
  btn.title = "Teilen";
  // Gezeichnet aus dem Zeichen-Vorrat: der Pfeil aus der Ablage, den iOS und Android fürs Teilen zeigen.
  btn.innerHTML = zeichen("teilen", 18);
  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    void teilen(input);
  });
  return btn;
}

interface BildBlaseEingabe {
  url: string;
  from: "user" | "bot";
  caption?: string | null;
  /** Öffnet das Bild groß (Lightbox). */
  vergroessern: (url: string, caption?: string | null) => void;
}

/** Bis zu dieser natürlichen Breite (Punkte) gilt ein Bild als klein: Es wird nicht aufgezogen, der Text steht daneben. */
export const KLEINES_BILD_BREITE = 320;

function baueBeschriftung(text: string): HTMLElement {
  const cap = document.createElement("div");
  cap.className = "bubble-caption";
  cap.textContent = text;
  return cap;
}

/**
 * Der Bild-Prompt eines erzeugten Bildes: kein Fließtext unter dem Bild, sondern eine kleine, standardmäßig
 * zugeklappte Zeile. `<details>` ist von Haus aus mit der Tastatur bedienbar und für Vorleser ausgezeichnet.
 */
function baueBildPrompt(text: string): HTMLElement {
  const d = document.createElement("details");
  d.className = "bubble-caption bubble-prompt";
  const sum = document.createElement("summary");
  sum.className = "bubble-prompt-titel";
  sum.textContent = "Prompt anzeigen";
  const p = document.createElement("p");
  p.className = "bubble-prompt-text";
  p.textContent = text;
  d.append(sum, p);
  return d;
}

/** Ein kleines Bild (natürliche Breite unter 320 Punkten) behält sein Maß; die Blase legt den Text daneben. */
function passeAnBildgroesse(node: HTMLElement, img: HTMLImageElement): void {
  const messen = (): void => {
    const b = img.naturalWidth;
    if (!(b > 0)) return;
    // Durchsichtiges Bild (PNG mit Alpha): abgesetzte Bildfläche, damit Umriss und Ausdehnung sichtbar sind.
    if (hatTransparenz(img)) node.dataset.alpha = "";
    if (b < KLEINES_BILD_BREITE) {
      node.dataset.klein = "";
      node.style.setProperty("--bild-breite", `${b}px`);
    }
  };
  if (img.complete) messen();
  img.addEventListener("load", messen, { once: true });
}

/** Die Bildblase (noch ohne Platz im Verlauf und ohne Uhrzeit). */
export function baueBildBlase({ url, from, caption, vergroessern }: BildBlaseEingabe): HTMLElement {
  const node = document.createElement("div");
  node.className = `bubble image ${from}`;
  const img = document.createElement("img");
  img.src = url;
  // Die Beschriftung steht sichtbar unter dem Bild: kein Alt-Text daraus (sonst liest ein Vorleser sie dreimal).
  img.alt = caption ? "" : "Bild";
  img.loading = "lazy";
  // Platz reservieren, BEVOR das Bild da ist — sonst wächst es beim
  // Heranscrollen von 0 auf seine Höhe und schiebt alles darunter weg.
  ruesteBildAus(img, url);
  img.addEventListener("click", () => vergroessern(url, caption));
  // Das Vergrößern ist auch mit der Tastatur erreichbar (Tab, Enter oder Leertaste).
  img.tabIndex = 0;
  img.setAttribute("role", "button");
  img.setAttribute("aria-label", "Bild vergrößern");
  img.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    ev.preventDefault();
    vergroessern(url, caption);
  });
  passeAnBildgroesse(node, img);
  node.appendChild(img);
  if (caption) {
    node.appendChild(from === "bot" ? baueBildPrompt(caption) : baueBeschriftung(caption));
  }
  node.appendChild(makeShareButton({ url, title: caption ?? "Bild", text: caption ?? "" }));
  node.appendChild(makeDownloadButton(url));
  return node;
}
